// src/App.jsx

// Imports -------------------------------------------------------------------------------------------------------------------------------
import React, { useState, useEffect } from "react";

import BMPSelectionTab from './components/BMPSelectionTab'
import WQAnalyteControls from './components/selectionControls/WQAnalyteControls'
import { WQPerformancePanel, HydrologyPerformancePanel, OverallPerformancePanel } from './components/PerformancePanelTabs';
import { MashupGoButton } from './components/widgetsAndUtils/GoButton';
import HydrologyControls from './components/selectionControls/HydrologyControls';

// Info Tabs
import OverviewTab from './components/OverviewTab';
import AnalysisMethodsTab from './components/AnalysisMethodsTab';
import InstructionsTab from './components/InstructionsTab';
import WQInfo from './components/infoSections/WQInfo';
import MPITab from './components/analysisPanels/mpi/MPITab';
import MPISelectionTable from './components/selectionControls/MPISelectionTable';
import ZoomInfo from './components/infoSections/ZoomInfo';
import DataDownloadTab from './components/DataDownloadTab';
import ToggleSwitch from './components/widgetsAndUtils/ToggleSwitch';

// Sub-tab components
import SubTab from './components/widgetsAndUtils/SubTab';
import WQPollutantRemovalPanel from './components/analysisPanels/wq/WQPollutantRemovalPanel';
import HydrologyFlowVolumePanel from './components/analysisPanels/hydrology/HydrologyFlowVolumePanel';

// Reducer Wrapper
import { useAppStateContext } from './hooks/useAppState';

// Tailwind build output
import "./styles/index.css"; 

// TEMPORARY:
// Commented out for now, Nov 17, 2025


// Tab ID's and Labels ------------------------------------------------------------------------------------------------------------------------------------------------
const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'methods', label: 'Analysis Methods' },
  { id: 'instructions', label: 'Instructions' },
  { id: 'bmp-selection', label: 'BMP Selection' },
  { id: 'wq', label: 'Water Quality' },
  { id: 'hydrology', label: 'Hydrology' },
  { id: 'mpi', label: 'Multi Parameter Index' },
  { id: 'data-download', label: 'Data Download' } // New tab
];

// Sub-tab configurations
const wqSubTabs = [
  { id: 'pollutant-removal', label: 'Pollutant Removal' },
  { id: 'performance-index', label: 'Performance Index' }
  
  // TEMPORARY: Just for now commented out
  //, { id: 'emc-over-time', label: 'EMC Output Over Time' } 

];

const hydrologySubTabs = [
  { id: 'flow-volume-reduction', label: 'Flow and Volume Reduction' },
  { id: 'performance-index', label: 'Performance Index' }
];

/* ──────────────────────────────── App Tabs ─────────────────────────────── */
export default function App() {
  
  // This is just a wrapper for a reducer
  // Can be found in useAppState.jsx in the hooks folder
  // basically a bunch of wrappers for dispatch functions to update an object that contains the major parts of the application's state
  const { state, bmpState, updateMpiComparisonMode } = useAppStateContext();
  
  // obvious what this does, but anyways, just the stateful variable for the active tab
  const [activeTab, setActiveTab] = useState('overview');

  // Sub-tab state management
  const [activeWQSubTab, setActiveWQSubTab] = useState('pollutant-removal');
  const [activeHydrologySubTab, setActiveHydrologySubTab] = useState('flow-volume-reduction');

  // simple stateful variables to toggle the display of that blue info box that lets them know they can brush and zoom into a scatterplot
  const [showWQZoomInfo, setShowWQZoomInfo] = useState(true);
  const [showHydrologyZoomInfo, setShowHydrologyZoomInfo] = useState(true);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle tab change and close mobile menu
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4 sm:p-6">
      {/* -------------------------------  Tab headers ----------------------------------------------- */}
      
      {/* Desktop Navigation - Hidden on mobile */}
      <div className="hidden md:flex space-x-2 border-b justify-center">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 rounded-t transition-colors
              ${activeTab === tab.id
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden border-b">
        {/* Mobile Header with Hamburger Button */}
        <div className="flex items-center justify-between py-3">
          <h1 className="text-lg font-semibold text-gray-900">
            {tabs.find(tab => tab.id === activeTab)?.label || 'Menu'}
          </h1>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="pb-3 space-y-1 bg-white border-t">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`block w-full text-left px-4 py-3 text-base font-medium transition-colors
                  ${activeTab === tab.id
                    ? "bg-blue-50 text-blue-600 border-r-4 border-blue-500"
                    : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* -------------------------------  Tab content - all tabs stay mounted ---------------------------------------------- */}
      {/* We are not using React tabs because we want to keep the tabs mounted and not unmount them when they are not active */}
      <div className="mt-6">
        {/* -------------------------------  Overview Tab ----------------------------------------------- */}
        <div className={activeTab === 'overview' ? 'block' : 'hidden'}>
          <OverviewTab />
        </div>
        
        
        {/* -------------------------------  Methods Tab ----------------------------------------------- */}
        <div className={activeTab === 'methods' ? 'block' : 'hidden'}>
          <AnalysisMethodsTab />
        </div>


        {/* -------------------------------  Instructions Tab ----------------------------------------------- */}
        <div className={activeTab === 'instructions' ? 'block' : 'hidden'}>
          <InstructionsTab />
        </div>

        {/* -------------------------------  BMP Selection Tab ----------------------------------------------- */}
        <div className={activeTab === 'bmp-selection' ? 'block' : 'hidden'}>
          <BMPSelectionTab />
        </div>

        {/* -------------------------------  Water Quality Tab ----------------------------------------------- */}
        {/* 
           The "GoButton" for the WQ Analyte section is within the WQAnalyteControls 
           From there it makes the appropriate fetch call to the API that provides the data for this application
        */}
        {/* The GoButton makes a fetch call to the API based on its props - it fetches the route that calculates wq or hydrology performance index */}
        <div className={activeTab === 'wq' ? 'block' : 'hidden'}>
          <WQInfo />
          
          <WQAnalyteControls />
          {/* 
            WQ Index data doesnt have a plot_data attribute. The keys are dynamically generated, and the keys are the analyte names.
            So we can just check and make sure there is at least one entry before rendering the WQ data visualization tabs
          */}
          { (Object.keys(state.wq.indexData || {}).length > 0) && (
          /* Sub-tabs for Water Quality */
          <SubTab 
            tabs={wqSubTabs}
            activeTab={activeWQSubTab}
            onTabChange={setActiveWQSubTab}
          >
            {/* Pollutant Removal Sub-tab */}
            <div className={activeWQSubTab === 'pollutant-removal' ? 'block' : 'hidden'}>
              { (Object.keys(state.wq.indexData).length > 0) && (
                <ZoomInfo // Letting them know they can brush on the scatterplot and Zoom if they wish. Only display if Hydrology index data exists
                  isVisible={showWQZoomInfo}
                  onToggle={() => setShowWQZoomInfo(!showWQZoomInfo)}
                  message={'You can drag your cursor over a section of the boxplots to zoom into certain sections of the data'}
                />
              )}
              <WQPollutantRemovalPanel />
            </div>

            {/* Performance Index Sub-tab */}
            <div className={activeWQSubTab === 'performance-index' ? 'block' : 'hidden'}>
              {/* ZoomInfo is that little blue section that lets them know they can brush on the plot to zoom in */}
              { (Object.keys(state.wq.indexData).length > 0) && (
                  <ZoomInfo 
                  isVisible={showWQZoomInfo}
                  onToggle={() => setShowWQZoomInfo(!showWQZoomInfo)}
                  />
                )}
              
              {/* Render a panel for each analyte in real API data */}
              {/* 
                Later, the threshold will be a sub-attribute under analyte, so there will be a different loop structure
                since the object entries will be like:
                {
                    "Nitrite as N, Total (mg/L)": [
                        {
                            threshold: 0.5,
                            plot_data: [{...},{...},{...},{...},{...}], // data for the scatterplot - all events
                            index_summary: [{...},{...}] // one for the actual selected BMP, and one for the comparison data
                        },
                        {
                            threshold: 0.3, // another threshold for comparison
                            plot_data: [{...},{...},{...},{...},{...}], // data for the scatterplot - all events
                            index_summary: [{...},{...}] // one for the actual selected BMP, and one for the comparison data
                        },
                        {
                            threshold: 0.1, // another threshold for comparison
                            plot_data: [{...},{...},{...},{...},{...}], // data for the scatterplot - all events
                            index_summary: [{...},{...}] // one for the actual selected BMP, and one for the comparison data
                        }
                    ],
                    "Phosphorus, Total (mg/L)": [
                        {
                            threshold: 0.5,
                            plot_data: [{...},{...},{...},{...},{...}], // data for the scatterplot - all events
                            index_summary: [{...},{...}] // one for the actual selected BMP, and one for the comparison data
                        }
                    ]

                    So probably the loop will be like:
                    state.wq.indexData && Object.entries(state.wq.indexData).map(([analyte, d]) => {
                      // d will be an array of objects with threshold, plot_data, and index_summary
                        return d.map((item, index) => (
                            <WQPerformancePanel
                            key={`${analyte}-${index}`}
                            analyteName={analyte}
                            plot_data={item.plot_data}
                            index_summary={item.index_summary}
                            threshold={item.threshold}
                            comparisonMode={state.wq.comparisonMode}
                            />
                        ));
                    })
              */}
              {state.wq.indexData && Object.entries(state.wq.indexData).map(([analyte, d]) => {

                // Find the threshold for this analyte
                const analyteConfig = state.wq.selectedAnalytes.find(a => a.analytename === analyte);
                const threshold = analyteConfig?.threshold;
                
                // Basically it returns a scatterplot and a performance index value gauge plot
                return (
                  <WQPerformancePanel
                    key={analyte}
                    analyteName={analyte}
                    plot_data={d.plot_data}
                    index_summary={d.index_summary}
                    threshold={threshold}
                    comparisonMode={state.wq.comparisonMode}
                  />
                );
              })}
            </div>

          </SubTab>
          )}
        </div>

        {/* -------------------------------  Hydrology Tab ----------------------------------------------- */}
        {/* Note that the HydrologyInfo component is a parent for the GoButton component */}
        {/* The GoButton makes a fetch call to the API based on its props - it fetches the route that calculates wq or hydrology performance index */}
        <div className={activeTab === 'hydrology' ? 'block' : 'hidden'}>
          
          <HydrologyControls />
          {state.hydrology.indexData && state.hydrology.indexData.plot_data && (
          /* Sub-tabs for Hydrology */
          <SubTab 
            tabs={hydrologySubTabs}
            activeTab={activeHydrologySubTab}
            onTabChange={setActiveHydrologySubTab}
          >
            {/* Flow and Volume Reduction Sub-tab */}
            <div className={activeHydrologySubTab === 'flow-volume-reduction' ? 'block' : 'hidden'}>
              { (Object.keys(state.hydrology.indexData).length > 0) && (
                <ZoomInfo // Letting them know they can brush on the scatterplot and Zoom if they wish. Only display if Hydrology index data exists
                  isVisible={showHydrologyZoomInfo}
                  onToggle={() => setShowHydrologyZoomInfo(!showHydrologyZoomInfo)}
                  message={'You can drag your cursor over a section of the boxplots to zoom into certain sections of the data'}
                />
              )}
              <HydrologyFlowVolumePanel />
            </div>

            {/* Performance Index Sub-tab */}
            <div className={activeHydrologySubTab === 'performance-index' ? 'block' : 'hidden'}>
              { (Object.keys(state.hydrology.indexData).length > 0) && (
                <ZoomInfo // Letting them know they can brush on the scatterplot and Zoom if they wish. Only display if Hydrology index data exists
                  isVisible={showHydrologyZoomInfo}
                  onToggle={() => setShowHydrologyZoomInfo(!showHydrologyZoomInfo)}
                />
              )}
              
             {/* Render a single panel for hydrology results */}
             {state.hydrology.indexData && state.hydrology.indexData.plot_data && (
               <HydrologyPerformancePanel
                 plot_data={state.hydrology.indexData.plot_data}
                 index_summary={state.hydrology.indexData.index_summary}
                 scatterPlotMargin={{ top: 30, right: 20, bottom: 60, left: 60 }}
                 comparisonMode={state.hydrology.comparisonMode}
               />
             )}

            </div>
          </SubTab>
          )}
        </div>



        {/* -------------------------------  Multi Parameter Index Tab ----------------------------------------------- */}
        {/* This is the tab that displays the MPI and the overall performance of the BMP Selection(s) */}
        <div className={activeTab === 'mpi' ? 'block' : 'hidden'}>
          <MPITab>
            <MPISelectionTable />
            <div className="flex items-center justify-between px-6 py-4 border-t mb-4">
              {bmpState.analysistype === 'individual' && (
                <ToggleSwitch
                  id="mpi-comparison-toggle"
                  checked={!!state.mpiComparisonMode}
                  onChange={e => updateMpiComparisonMode(e.target.checked)}
                  label="Show Comparison Data"
                />
              )}
              <MashupGoButton />
            </div>
            {/* Overall Performance Panel (BarChart + Gauge) */}
            <OverallPerformancePanel />
          </MPITab>
        </div>



        {/* -------------------------------  Data Download Tab ----------------------------------------------- */}
        <div className={activeTab === 'data-download' ? 'block' : 'hidden'}>
          <DataDownloadTab />
        </div>

      </div>
    </div>
  );
}
